// Multi-year dancer festival history helpers.
// Competition results: shared collection iabf-competition-results/{year}.
// Showcase pieces: year-scoped iabf-pieces-{year} (enrollment id per year).

import {
  IABF_DANCER_YEAR,
  dancersCollection,
  col
} from '@/config/iabf-event'
import { normalizeEmail } from '@/utils/iabf-people'

export const COMPETITION_RESULTS_COLLECTION = 'iabf-competition-results'

/** Bulk-imported showcase pieces + recording URLs per year (2022–2025 history). */
export const SHOWCASE_HISTORY_COLLECTION = 'iabf-showcase-history'

/** Years that live in the showcase/competition archive even without an enrollment doc. */
export const ARCHIVE_FESTIVAL_YEARS = [2022, 2023, 2024, 2025, 2026]

/**
 * Normalize a person name for loose matching (first/last, accents-ish).
 */
export function normalizePersonName (name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Scorecard / nickname first names → canonical first names used on rosters. */
const FIRST_NAME_ALIASES = {
  kimmie: 'kimberly',
  kim: 'kimberly',
  barb: 'barbara',
  jenny: 'jennifer',
  jen: 'jennifer',
  neilla: 'neila',
  enn: 'erin',
  jhn: 'john',
  patty: 'patricia',
  pat: 'patricia',
  sandy: 'sandra'
}

/** Archive typos / short last names → roster last-name tokens. */
const LAST_NAME_ALIASES = {
  strumf: 'strumpf',
  noguiera: 'nogueira',
  forston: 'fortson'
}

/** Program / maiden / nickname full names → roster first + last. */
const PROGRAM_NAME_ALIASES = {
  'julie gill': { firstName: 'Julie', lastName: 'Leung' },
  'kris pierce': { firstName: 'Kris', lastName: 'Roberts' }
}

/** Split a normalized person name into matching tokens. */
export function nameTokens (name) {
  return normalizePersonName(name).split(/[\s-]+/).filter(Boolean)
}

function lastNameTokens (lastName) {
  return nameTokens(lastName).map(t => LAST_NAME_ALIASES[t] || t)
}

export function lastNameCompatible (a, b) {
  const significant = (s) => {
    const tokens = lastNameTokens(s)
    const long = tokens.filter(t => t.length > 1)
    return long.length ? long : tokens
  }
  const ta = significant(a)
  const tb = significant(b)
  if (!ta.length || !tb.length) {
    return false
  }
  // "Meyer" vs "Meyer-Heß" vs "Meyer Hess" — same family name stem.
  // "M. Segal" vs "Segal" — drop the middle initial so the family name matches.
  return ta[0] === tb[0]
}

/**
 * Whole-token name match. Never substring ("Zu" must not hit "Suzuki").
 * Also finds "First Last" as adjacent tokens inside a comma-separated blob.
 */
export function entryNameMatchesPerson (entryName, firstName, lastName) {
  const parts = nameTokens(entryName)
  const fn = nameTokens(firstName)[0] || ''
  const lnTokens = nameTokens(lastName)
  if (!parts.length || (!fn && !lnTokens.length)) {
    return false
  }
  const full = [fn, ...lnTokens].filter(Boolean).join(' ')
  const n = parts.join(' ')
  if (full && (n === full || n.endsWith(' ' + full))) {
    return true
  }
  if (!fn || !lnTokens.length) {
    return false
  }
  for (let i = 0; i < parts.length; i++) {
    if (!firstNamesCompatible(parts[i], fn)) {
      continue
    }
    const after = parts.slice(i + 1)
    if (lnTokens.every((t, j) => after[j] === t)) {
      return true
    }
    if (after.length && lastNameCompatible(after.join(' '), lnTokens.join(' '))) {
      return true
    }
  }
  return false
}

export function canonicalFirstName (firstName) {
  const n = normalizePersonName(firstName).split(/\s+/)[0] || ''
  if (!n) {
    return ''
  }
  // Strip "Dr." already handled by normalize; "dr neila" → neila
  if (n === 'dr') {
    return ''
  }
  return FIRST_NAME_ALIASES[n] || n
}

/** True when two first names are the same person (Kimmie = Kimberly). */
export function firstNamesCompatible (a, b) {
  const ta = nameTokens(a)[0] || ''
  const tb = nameTokens(b)[0] || ''
  if (!ta || !tb) {
    return false
  }
  if (ta === tb) {
    return true
  }
  return canonicalFirstName(ta) === canonicalFirstName(tb)
}

/**
 * Build a roster index for matching competition / history rows to dancers.
 * @param {Array<{ firstName?: string, lastName?: string, email?: string }>} dancers
 */
export function buildDancerMatchIndex (dancers) {
  const byFirst = new Map()
  const list = []
  for (const d of dancers || []) {
    const firstName = String(d.firstName || '').trim()
    const lastName = String(d.lastName || '').trim()
    const email = normalizeEmail(d.email)
    if (!firstName && !lastName && !email) {
      continue
    }
    const entry = {
      firstName,
      lastName,
      email,
      firstKey: canonicalFirstName(firstName),
      lastKey: normalizePersonName(lastName)
    }
    // Also index "Dr. Neila" → first token after title
    if (!entry.firstKey && firstName) {
      const parts = normalizePersonName(firstName).split(/\s+/).filter(p => p !== 'dr')
      entry.firstKey = canonicalFirstName(parts[0] || '')
    }
    list.push(entry)
    if (entry.firstKey) {
      if (!byFirst.has(entry.firstKey)) {
        byFirst.set(entry.firstKey, [])
      }
      byFirst.get(entry.firstKey).push(entry)
    }
  }
  return { byFirst, list }
}

/**
 * Match a result row to a roster dancer.
 * - first + last (when last provided): unique match only
 * - first only: unique first name on roster only; ambiguous → no match
 *
 * @returns {{ matched: boolean, ambiguous: boolean, dancer: object|null, reason: string }}
 */
export function matchResultToDancer (row, index) {
  if (!row || !index) {
    return { matched: false, ambiguous: false, dancer: null, reason: 'empty' }
  }
  const firstKey = canonicalFirstName(row.firstName)
  const lastKey = normalizePersonName(row.lastName)
  const email = normalizeEmail(row.email)

  if (email) {
    const byEmail = index.list.filter(d => d.email && d.email === email)
    if (byEmail.length === 1) {
      return { matched: true, ambiguous: false, dancer: byEmail[0], reason: 'email' }
    }
  }

  if (!firstKey) {
    return { matched: false, ambiguous: false, dancer: null, reason: 'no-first' }
  }

  const candidates = index.byFirst.get(firstKey) || []
  if (!candidates.length) {
    // try raw first without alias if alias failed
    const raw = normalizePersonName(row.firstName).split(/\s+/)[0]
    const alt = (index.byFirst.get(raw) || [])
    if (alt.length === 1 && !lastKey) {
      return { matched: true, ambiguous: false, dancer: alt[0], reason: 'first-unique' }
    }
    if (lastKey && alt.length) {
      const withLast = alt.filter(d => d.lastKey === lastKey || d.lastKey.startsWith(lastKey) || lastKey.startsWith(d.lastKey))
      if (withLast.length === 1) {
        return { matched: true, ambiguous: false, dancer: withLast[0], reason: 'first-last' }
      }
      if (withLast.length > 1) {
        return { matched: false, ambiguous: true, dancer: null, reason: 'ambiguous-last' }
      }
    }
    return { matched: false, ambiguous: false, dancer: null, reason: 'not-found' }
  }

  if (lastKey) {
    const withLast = candidates.filter(d =>
      d.lastKey === lastKey ||
      d.lastKey.startsWith(lastKey) ||
      lastKey.startsWith(d.lastKey)
    )
    if (withLast.length === 1) {
      return { matched: true, ambiguous: false, dancer: withLast[0], reason: 'first-last' }
    }
    if (withLast.length > 1) {
      return { matched: false, ambiguous: true, dancer: null, reason: 'ambiguous-last' }
    }
    // last name typed but no match — don't fall back to first-only
    return { matched: false, ambiguous: false, dancer: null, reason: 'last-no-match' }
  }

  // First name only — require uniqueness
  if (candidates.length === 1) {
    return { matched: true, ambiguous: false, dancer: candidates[0], reason: 'first-unique' }
  }
  if (candidates.length > 1) {
    return { matched: false, ambiguous: true, dancer: null, reason: 'ambiguous-first' }
  }
  return { matched: false, ambiguous: false, dancer: null, reason: 'not-found' }
}

/**
 * Apply match result onto a row (fills last name + email from roster; keeps typed first if set).
 * Does not clear fields when unmatched.
 */
export function applyDancerMatchToRow (row, match) {
  if (!row || !match || !match.matched || !match.dancer) {
    return { ...row, matchStatus: match ? match.reason : 'empty' }
  }
  const d = match.dancer
  return {
    ...row,
    firstName: d.firstName || row.firstName,
    lastName: d.lastName || row.lastName,
    email: d.email || row.email,
    matchStatus: match.reason
  }
}

export function parseArchiveDancerNames (piece) {
  const names = Array.isArray(piece && piece.dancerNames)
    ? piece.dancerNames.map(s => String(s || '').trim()).filter(Boolean)
    : []
  if (names.length) {
    return names
  }
  const raw = String((piece && piece.dancerNamesRaw) || '').trim()
  if (!raw) {
    return []
  }
  return raw.split(/,|\band\b|&/i).map(s => s.trim()).filter(Boolean)
}

/** Stable first+last key for linking archive names to people. */
export function personLinkKey (firstName, lastName) {
  const fn = canonicalFirstName(firstName)
  const ln = lastNameTokens(lastName).join(' ')
  if (!fn || !ln) {
    return ''
  }
  return fn + '|' + ln
}

function parseLinkName (name) {
  const parts = nameTokens(name)
  let start = 0
  if (parts[0] === 'dr') {
    start = 1
  }
  if (parts.length - start < 2) {
    return null
  }
  const lastParts = parts.slice(start + 1)
  return {
    first: parts[start],
    last: lastParts.join(' '),
    lastToken: lastParts[lastParts.length - 1]
  }
}

export function nameStringToLinkKey (name) {
  const parsed = parseLinkName(name)
  if (!parsed) {
    return ''
  }
  return personLinkKey(parsed.first, parsed.last)
}

function livePersonId (personId, byId) {
  let cur = String(personId || '').trim()
  const seen = new Set()
  while (cur && !seen.has(cur)) {
    seen.add(cur)
    const row = byId.get(cur)
    const next = row && String(row.mergedInto || '').trim()
    if (!next) {
      return cur
    }
    cur = next
  }
  return String(personId || '').trim()
}

export function peopleForArchiveLinkYear (year, { people = [], enrollments = [] } = {}) {
  void year
  const byId = new Map()
  people.forEach(p => {
    if (p && p.id) {
      byId.set(String(p.id), p)
    }
  })
  const out = []
  enrollments.forEach(e => {
    const rawId = String((e && e.personId) || '').trim()
    if (!rawId) {
      return
    }
    const id = livePersonId(rawId, byId)
    const live = byId.get(id) || {}
    out.push({
      id,
      personId: id,
      firstName: live.firstName || e.firstName,
      lastName: live.lastName || e.lastName
    })
    if (e.firstName && e.lastName) {
      out.push({
        id,
        personId: id,
        firstName: e.firstName,
        lastName: e.lastName
      })
    }
  })
  people.forEach(p => {
    if (!p || !p.id || p.mergedInto) {
      return
    }
    const email = String(p.email || '').toLowerCase()
    if (email.indexOf('merged+') === 0 || email.indexOf('@iabf.invalid') !== -1) {
      return
    }
    if (email.indexOf('+') !== -1) {
      return
    }
    out.push({
      id: p.id,
      personId: p.id,
      firstName: p.firstName,
      lastName: p.lastName
    })
  })
  return out
}

export function buildPersonLinkIndex (people) {
  const byKey = new Map()
  const list = []
  for (const p of people || []) {
    const id = String((p && (p.personId || p.id)) || '').trim()
    if (!id) {
      continue
    }
    const key = personLinkKey(p.firstName, p.lastName)
    if (!key) {
      continue
    }
    if (!byKey.has(key)) {
      byKey.set(key, [])
    }
    const ids = byKey.get(key)
    if (ids.indexOf(id) === -1) {
      ids.push(id)
    }
    list.push({
      id,
      firstName: p.firstName,
      lastName: p.lastName,
      firstKey: canonicalFirstName(p.firstName),
      lastKey: lastNameTokens(p.lastName).join(' ')
    })
  }
  return { byKey, list }
}

function uniqueId (ids) {
  const uniq = []
  ids.forEach(id => {
    if (id && uniq.indexOf(id) === -1) {
      uniq.push(id)
    }
  })
  return uniq.length === 1 ? uniq[0] : null
}

function programNameAlias (name) {
  const key = normalizePersonName(name)
  return PROGRAM_NAME_ALIASES[key] || null
}

export function resolveArchiveNameToPersonId (name, index) {
  if (!index || !index.byKey) {
    return null
  }
  const alias = programNameAlias(name)
  if (alias) {
    const aliased = uniqueId(index.byKey.get(personLinkKey(alias.firstName, alias.lastName)) || [])
    if (aliased) {
      return aliased
    }
  }
  const firstOnly = nameTokens(name)
  if (firstOnly.length === 1) {
    const first = canonicalFirstName(firstOnly[0])
    const hits = (index.list || []).filter(p => p.firstKey === first)
    return uniqueId(hits.map(p => p.id))
  }
  const parsed = parseLinkName(name)
  if (!parsed) {
    return null
  }
  const exact = uniqueId(index.byKey.get(personLinkKey(parsed.first, parsed.last)) || [])
  if (exact) {
    return exact
  }
  const droppedMiddle = uniqueId(index.byKey.get(personLinkKey(parsed.first, parsed.lastToken)) || [])
  if (droppedMiddle) {
    return droppedMiddle
  }
  const first = canonicalFirstName(parsed.first)
  const candidates = (index.list || []).filter(p => p.firstKey === first)
  const compatible = candidates.filter(p =>
    lastNameCompatible(parsed.last, p.lastName) ||
    lastNameCompatible(parsed.lastToken, p.lastName)
  )
  return uniqueId(compatible.map(p => p.id))
}

export function linkArchivePieceToPeople (piece, index) {
  const dancerNames = parseArchiveDancerNames(piece)
  const dancerPersonIds = []
  const unlinkedNames = []
  const seen = new Set()
  dancerNames.forEach(name => {
    const id = resolveArchiveNameToPersonId(name, index)
    if (!id) {
      unlinkedNames.push(name)
      return
    }
    if (!seen.has(id)) {
      seen.add(id)
      dancerPersonIds.push(id)
    }
  })
  return { dancerNames, dancerPersonIds, unlinkedNames }
}

/**
 * True if this person is linked on a showcase-history piece.
 * Read path uses personId only — names are resolved at import/save.
 */
export function historyPersonIdList ({ personId, personIds } = {}) {
  const out = []
  const seen = {}
  const add = (id) => {
    const s = String(id || '').trim()
    if (!s || seen[s]) {
      return
    }
    seen[s] = true
    out.push(s)
  }
  if (Array.isArray(personIds)) {
    personIds.forEach(add)
  }
  add(personId)
  return out
}

export function showcaseHistoryPieceMatchesDancer (piece, identity = {}) {
  if (!piece) {
    return false
  }
  const mine = historyPersonIdList(identity)
  if (!mine.length) {
    return false
  }
  const ids = Array.isArray(piece.dancerPersonIds) ? piece.dancerPersonIds : []
  for (let i = 0; i < mine.length; i++) {
    if (ids.indexOf(mine[i]) !== -1) {
      return true
    }
  }
  return false
}

/** Expected CSV columns (header row optional if order matches). */
export const COMPETITION_CSV_HEADERS = [
  'email',
  'firstName',
  'lastName',
  'pieceTitle',
  'division',
  'placement'
]

/**
 * Parse a CSV line respecting double-quoted fields.
 * @param {string} line
 * @returns {string[]}
 */
export function splitCsvLine (line) {
  const out = []
  let cur = ''
  let inQuotes = false
  const s = String(line || '')
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      out.push(cur.trim())
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur.trim())
  return out
}

function looksLikeHeader (cells) {
  const joined = cells.map(c => c.toLowerCase().replace(/\s+/g, '')).join('|')
  return joined.includes('email') || joined.includes('piecetitle') || joined.includes('placement')
}

function mapHeaderIndex (cells) {
  const map = {}
  cells.forEach((raw, i) => {
    const key = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '')
    if (!key) {
      return
    }
    if (key === 'email' || key === 'danceremail') {
      map.email = i
    } else if (key === 'firstname' || key === 'first') {
      map.firstName = i
    } else if (key === 'lastname' || key === 'last') {
      map.lastName = i
    } else if (key === 'piecetitle' || key === 'piece' || key === 'title') {
      map.pieceTitle = i
    } else if (key === 'division' || key === 'category') {
      map.division = i
    } else if (key === 'placement' || key === 'place' || key === 'award' || key === 'result') {
      map.placement = i
    } else if (key === 'notes' || key === 'note') {
      map.notes = i
    }
  })
  return map
}

/** Place values entered on live (operable-year) competition rows. */
export const COMPETITION_PLACE_OPTIONS = [
  { value: '', text: '—' },
  { value: '1st Place', text: '1st' },
  { value: '2nd Place', text: '2nd' },
  { value: '3rd Place', text: '3rd' }
]

/**
 * Canonical podium / JC label used on competition rows and dancer history.
 */
export function canonicalCompetitionPlacement (raw) {
  const p = String(raw || '').trim()
  if (!p) {
    return ''
  }
  if (/judge/i.test(p)) {
    return "Judges' Choice"
  }
  if (p === '1' || /^1st(\s+place)?$/i.test(p)) {
    return '1st Place'
  }
  if (p === '2' || /^2nd(\s+place)?$/i.test(p)) {
    return '2nd Place'
  }
  if (p === '3' || /^3rd(\s+place)?$/i.test(p)) {
    return '3rd Place'
  }
  return p
}

export function isPodiumPlacement (placement) {
  const c = canonicalCompetitionPlacement(placement)
  return c === '1st Place' || c === '2nd Place' || c === '3rd Place'
}

export function isCompetitionRowPlacement (placement) {
  return isPodiumPlacement(placement)
}

export function normalizeSpecialAward (raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const name = String(raw.name || raw.placement || raw.title || '').trim()
  if (!name) {
    return null
  }
  const id = String(raw.id || '').trim() || specialAwardIdFromName(name)
  return {
    id,
    name,
    pieceId: String(raw.pieceId || '').trim()
  }
}

export function specialAwardIdFromName (name) {
  const slug = normalizePersonName(name).replace(/\s+/g, '-') || 'award'
  return 'jc-' + slug
}

/**
 * Named Judges' Choice / special awards (not 1st/2nd/3rd on a division row).
 */
export function isNamedSpecialAwardResult (result) {
  if (!result) {
    return false
  }
  if (result.source === 'special-award') {
    return true
  }
  if (isPodiumPlacement(result.placement)) {
    return false
  }
  const placement = String(result.placement || '').trim()
  if (!placement) {
    return false
  }
  if (canonicalCompetitionPlacement(placement) === "Judges' Choice") {
    return true
  }
  return normalizeCompetitionDivision(result.division) === 'special award'
}

function resultMatchesPerson (result, person) {
  if (!result || !person) {
    return false
  }
  if (competitionResultMatchesDancer(result, person)) {
    return true
  }
  const fn = canonicalFirstName(person.firstName)
  const rfn = canonicalFirstName(result.firstName)
  return !!(fn && rfn && fn === rfn && lastNameCompatible(person.lastName, result.lastName))
}

/**
 * Collapse imported special-award result rows into one award per name.
 */
export function specialAwardsFromImportedResults (results) {
  const byName = new Map()
  for (const r of results || []) {
    if (!isNamedSpecialAwardResult(r)) {
      continue
    }
    const name = String(r.placement || '').trim()
    if (!name) {
      continue
    }
    const key = normalizePersonName(name)
    if (!byName.has(key)) {
      byName.set(key, {
        id: specialAwardIdFromName(name),
        name,
        pieceId: String(r.pieceId || '').trim(),
        importedDancers: []
      })
    }
    const g = byName.get(key)
    if (r.pieceId && !g.pieceId) {
      g.pieceId = String(r.pieceId).trim()
    }
    if (r.firstName || r.lastName || r.email || r.dancerId) {
      g.importedDancers.push({
        firstName: r.firstName || '',
        lastName: r.lastName || '',
        email: r.email || '',
        dancerId: r.dancerId || ''
      })
    }
  }
  return Array.from(byName.values())
}

/**
 * Find the competition row that won this special award (piece id, then dancer names).
 */
export function matchSpecialAwardToRow (award, rows) {
  if (!award || !Array.isArray(rows)) {
    return null
  }
  if (award.pieceId) {
    const byId = rows.find(r => r.pieceId && r.pieceId === award.pieceId)
    if (byId) {
      return byId
    }
  }
  const dancers = award.importedDancers || []
  if (!dancers.length) {
    return null
  }
  let best = null
  let bestHits = 0
  for (const row of rows) {
    if (!row.pieceId) {
      continue
    }
    let hits = 0
    for (const d of dancers) {
      const hit = (row.entrants || []).some((e) => resultMatchesPerson({
        email: d.email,
        firstName: d.firstName,
        lastName: d.lastName
      }, e))
      if (hit) {
        hits++
      }
    }
    if (hits > bestHits) {
      bestHits = hits
      best = row
    }
  }
  return bestHits > 0 ? best : null
}

/**
 * Normalize division labels so archive imports match live rows
 * (“Int/Adv Pointe” → “Intermediate/Advanced Pointe”).
 */
export function normalizeCompetitionDivision (raw) {
  let s = normalizePersonName(raw)
  s = s.replace(/\bint\s*\/?\s*adv\b/g, 'intermediate advanced')
  s = s.replace(/\bbeg\b/g, 'beginner')
  s = s.replace(/\bint\b/g, 'intermediate')
  s = s.replace(/\badv\b/g, 'advanced')
  s = s.replace(/\bpro\b/g, 'professional')
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

export function competitionScoreFromResult (result) {
  if (!result) {
    return ''
  }
  const direct = String(result.score || '').trim()
  if (direct) {
    return direct
  }
  const m = String(result.notes || '').match(/(\d+(?:\.\d+)?)\s*pts/i)
  return m ? m[1] : ''
}

/**
 * Normalize one competition result row for storage / display.
 * @returns {{ email: string, firstName: string, lastName: string, pieceTitle: string, division: string, placement: string, notes: string, pieceId: string, dancerId: string, score: string, source: string } | null}
 */
export function normalizeCompetitionResult (raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const email = normalizeEmail(raw.email)
  const pieceTitle = String(raw.pieceTitle || raw.piece || raw.title || '').trim()
  const placement = String(raw.placement || raw.award || raw.result || '').trim()
  const firstName = String(raw.firstName || '').trim()
  const lastName = String(raw.lastName || '').trim()
  const division = String(raw.division || '').trim()
  const notes = String(raw.notes || '').trim()
  const pieceId = String(raw.pieceId || '').trim()
  const dancerId = String(raw.dancerId || '').trim()
  const personId = String(raw.personId || '').trim()
  const score = String(raw.score || '').trim()
  const source = String(raw.source || '').trim()
  // Need a person (email/name/id) OR a piece title (group awards),
  // plus something to show (piece or placement).
  if (!email && !firstName && !lastName && !pieceTitle && !personId) {
    return null
  }
  if (!pieceTitle && !placement) {
    return null
  }
  return {
    email,
    firstName,
    lastName,
    pieceTitle,
    division,
    placement,
    notes,
    pieceId,
    dancerId,
    personId,
    score,
    source
  }
}

/**
 * True when an existing year-results row should be replaced by a live
 * place/score save for this competition piece.
 */
export function shouldReplaceResultForPiece (result, { pieceId, pieceTitle, division, dancers } = {}) {
  if (!result) {
    return false
  }
  if (pieceId && result.pieceId === pieceId) {
    return true
  }
  if (result.source === 'competition-row' && pieceId && result.pieceId === pieceId) {
    return true
  }
  // Special awards (Judges' Choice named awards, etc.) stay until edited
  // on the archive-style results page.
  if (!isPodiumPlacement(result.placement)) {
    return false
  }
  const titleA = normalizePersonName(result.pieceTitle)
  const titleB = normalizePersonName(pieceTitle)
  const titleMatch = !!(titleA && titleB && titleA === titleB)
  const emptyTitle = !titleA
  const divMatch = !result.division || !division ||
    normalizeCompetitionDivision(result.division) === normalizeCompetitionDivision(division)
  const dancerMatch = (dancers || []).some((d) => {
    if (competitionResultMatchesDancer(result, d)) {
      return true
    }
    const fn = canonicalFirstName(d.firstName)
    const rfn = canonicalFirstName(result.firstName)
    return !!(fn && rfn && fn === rfn && lastNameCompatible(d.lastName, result.lastName))
  })
  if (titleMatch && (dancerMatch || emptyTitle || !result.firstName)) {
    return true
  }
  if (emptyTitle && dancerMatch && divMatch) {
    return true
  }
  return false
}

/**
 * Pick a stored result that belongs on this competition-registration row
 * (for hydrating 2026 archive imports onto live rows).
 */
export function matchStoredResultToCompetitionRow (result, row) {
  if (!result || !row) {
    return false
  }
  if (!isCompetitionRowPlacement(result.placement) && !result.score) {
    return false
  }
  if (row.pieceId && result.pieceId === row.pieceId) {
    return true
  }
  const titleA = normalizePersonName(result.pieceTitle)
  const titleB = normalizePersonName(row.pieceTitle)
  if (titleA && titleB && titleA === titleB) {
    return true
  }
  const divMatch = !result.division || !row.division ||
    normalizeCompetitionDivision(result.division) === normalizeCompetitionDivision(row.division)
  if (!divMatch) {
    return false
  }
  return (row.entrants || []).some((e) => {
    if (competitionResultMatchesDancer(result, {
      email: e.email,
      firstName: e.firstName,
      lastName: e.lastName
    })) {
      return true
    }
    const fn = canonicalFirstName(e.firstName)
    const rfn = canonicalFirstName(result.firstName)
    return !!(fn && rfn && fn === rfn && lastNameCompatible(e.lastName, result.lastName))
  })
}

/**
 * Parse competition-results CSV text into normalized rows.
 * Header row optional. Default column order:
 * email, firstName, lastName, pieceTitle, division, placement
 *
 * @param {string} text
 * @returns {{ rows: object[], errors: string[], skipped: number }}
 */
export function parseCompetitionResultsCsv (text) {
  const cleaned = String(text || '').replace(/^\uFEFF/, '').trim()
  if (!cleaned) {
    return { rows: [], errors: ['Paste at least one data row.'], skipped: 0 }
  }
  const lines = cleaned.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (!lines.length) {
    return { rows: [], errors: ['Paste at least one data row.'], skipped: 0 }
  }

  let start = 0
  let colMap = {
    email: 0,
    firstName: 1,
    lastName: 2,
    pieceTitle: 3,
    division: 4,
    placement: 5,
    notes: 6
  }
  const firstCells = splitCsvLine(lines[0])
  if (looksLikeHeader(firstCells)) {
    const mapped = mapHeaderIndex(firstCells)
    if (mapped.email !== undefined || mapped.pieceTitle !== undefined || mapped.placement !== undefined) {
      colMap = { ...colMap, ...mapped }
      start = 1
    }
  }

  const rows = []
  const errors = []
  let skipped = 0
  for (let i = start; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i])
    const cell = (idx) => (idx === undefined || idx < 0 ? '' : (cells[idx] || ''))
    const raw = {
      email: cell(colMap.email),
      firstName: cell(colMap.firstName),
      lastName: cell(colMap.lastName),
      pieceTitle: cell(colMap.pieceTitle),
      division: cell(colMap.division),
      placement: cell(colMap.placement),
      notes: cell(colMap.notes)
    }
    const row = normalizeCompetitionResult(raw)
    if (!row) {
      skipped++
      errors.push(`Line ${i + 1}: skipped (need email or name, and piece or placement).`)
      continue
    }
    rows.push(row)
  }
  return { rows, errors, skipped }
}

/**
 * True if a competition result belongs to this dancer (email preferred, else name).
 */
export function competitionResultMatchesDancer (result, { email, firstName, lastName, personId, personIds } = {}) {
  if (!result) {
    return false
  }
  const mine = historyPersonIdList({ personId, personIds })
  if (result.personId && mine.indexOf(String(result.personId)) !== -1) {
    return true
  }
  const e = normalizeEmail(email)
  if (e && result.email && result.email === e) {
    return true
  }
  const fn = nameTokens(firstName)[0] || ''
  const ln = String(lastName || '').trim()
  const rfn = nameTokens(result.firstName)[0] || ''
  const rln = String(result.lastName || '').trim()
  if (fn && ln && rfn && rln && firstNamesCompatible(fn, rfn) && lastNameCompatible(ln, rln)) {
    return true
  }
  return false
}

function competitionResultRichness (result) {
  if (!result) {
    return 0
  }
  return (result.pieceTitle ? 8 : 0) +
    (result.email ? 4 : 0) +
    (result.score ? 2 : 0) +
    (result.pieceId ? 2 : 0) +
    (result.source === 'competition-row' ? 1 : 0)
}

function mergeCompetitionResultPair (a, b) {
  const rich = competitionResultRichness(a) >= competitionResultRichness(b) ? a : b
  const other = rich === a ? b : a
  return {
    ...other,
    ...rich,
    email: rich.email || other.email,
    pieceTitle: rich.pieceTitle || other.pieceTitle,
    score: rich.score || other.score,
    notes: rich.notes || other.notes,
    pieceId: rich.pieceId || other.pieceId,
    dancerId: rich.dancerId || other.dancerId,
    division: (rich.division && rich.division.length >= String(other.division || '').length)
      ? rich.division
      : (other.division || rich.division)
  }
}

function samePodiumCompetitionResult (a, b) {
  if (normalizePersonName(a.placement) !== normalizePersonName(b.placement)) {
    return false
  }
  const da = normalizeCompetitionDivision(a.division)
  const db = normalizeCompetitionDivision(b.division)
  if (!da || !db) {
    return true
  }
  return da === db
}

function sameSpecialCompetitionResult (a, b) {
  if (normalizePersonName(a.placement) !== normalizePersonName(b.placement)) {
    return false
  }
  const ta = pieceTitleKey(a.pieceTitle || a.notes)
  const tb = pieceTitleKey(b.pieceTitle || b.notes)
  return !!(ta && tb && (ta === tb || ta.startsWith(tb) || tb.startsWith(ta)))
}

/**
 * Archive import + live competition-row dual-write can store the same
 * 1st/2nd/3rd twice (one with email/piece, one without). Call after the
 * list is already filtered to one dancer.
 */
export function dedupeCompetitionResults (results) {
  const podium = []
  const specials = []
  for (const raw of results || []) {
    if (!raw) {
      continue
    }
    if (isNamedSpecialAwardResult(raw) || (raw.placement && !isPodiumPlacement(raw.placement))) {
      specials.push(raw)
    } else {
      podium.push(raw)
    }
  }
  const mergeList = (list, same) => {
    const out = []
    for (const row of list) {
      const idx = out.findIndex(prev => same(prev, row))
      if (idx === -1) {
        out.push(row)
      } else {
        out[idx] = mergeCompetitionResultPair(out[idx], row)
      }
    }
    return out
  }
  return mergeList(podium, samePodiumCompetitionResult)
    .concat(mergeList(specials, sameSpecialCompetitionResult))
}

/**
 * True when a placement string is a gold / 1st-place medal (not Judges' Choice).
 * @param {string} placement
 */
export function isGoldPlacement (placement) {
  const p = String(placement || '').trim().toLowerCase()
  if (!p) {
    return false
  }
  if (p.includes('judge')) {
    return false
  }
  if (/\bgold\b/.test(p)) {
    return true
  }
  // "1", "1st", "1st Place", "1st place — ..."
  if (/^1(st)?(\s+place)?\b/.test(p)) {
    return true
  }
  return false
}

/**
 * Gold medals from loadDancerFestivalHistory year panels (newest first).
 * @param {Array<{ year: number, competition?: object[] }>} yearPanels
 * @returns {Array<{ year: number, division: string, pieceTitle: string, placement: string }>}
 */
export function priorGoldMedalsFromYearPanels (yearPanels) {
  const golds = []
  for (const panel of yearPanels || []) {
    const year = Number(panel && panel.year)
    for (const r of (panel && panel.competition) || []) {
      if (!isGoldPlacement(r.placement)) {
        continue
      }
      golds.push({
        year: Number.isFinite(year) ? year : 0,
        division: String(r.division || '').trim(),
        pieceTitle: String(r.pieceTitle || '').trim(),
        placement: String(r.placement || 'Gold').trim()
      })
    }
  }
  golds.sort((a, b) => b.year - a.year)
  return golds
}

/**
 * Compact string for survey.previousGold / admin signals.
 * e.g. "2024 Beg Original Choreo; 2023 Adv Solo Variation"
 * @param {Array<{ year: number, division: string, pieceTitle: string }>} golds
 */
export function formatPriorGoldSummary (golds) {
  if (!golds || !golds.length) {
    return ''
  }
  return golds.map(g => {
    const label = g.division || g.pieceTitle || 'Competition'
    return g.year ? `${g.year} ${label}` : label
  }).join('; ')
}

/**
 * True when this dancer has a finished IABF year to look back on.
 * Current-year-only (first festival) and canceled-only years do not count.
 */
export function hasPastFestivalYears ({
  yearsAttended,
  enrollmentYear,
  isCanceledAccount,
  canceledYears
} = {}) {
  const canceled = new Set()
  for (const raw of canceledYears || []) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) {
      canceled.add(n)
    }
  }
  const isPast = (raw) => {
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 && n !== IABF_DANCER_YEAR && !canceled.has(n)
  }
  for (const y of yearsAttended || []) {
    if (isPast(y)) {
      return true
    }
  }
  if (!isCanceledAccount && isPast(enrollmentYear)) {
    return true
  }
  return false
}

export function yearsForFestivalHistory ({ yearsAttended, enrollmentYear } = {}) {
  const set = new Set()
  for (const y of yearsAttended || []) {
    const n = Number(y)
    if (Number.isFinite(n) && n > 0) {
      set.add(n)
    }
  }
  const en = Number(enrollmentYear)
  if (Number.isFinite(en) && en > 0) {
    set.add(en)
  }
  // Intentionally no fallback to IABF_DANCER_YEAR alone — empty history is correct
  // when the person has no years and no live enrollment.
  return Array.from(set).sort((a, b) => b - a)
}

export function piecesCollectionName (year) {
  return col('pieces', year)
}

export function dancersCollectionName (year) {
  return dancersCollection(year)
}

/**
 * 2026 (and same-shaped later years) store corps casting on the enrollment
 * as dancer.becoming.participating, etc. History reads those flags — they
 * are not iabf-pieces rows.
 */
export const CORPS_HISTORY_FIELDS = [
  { field: 'aliceInWonderland', name: 'Alice in Wonderland' },
  { field: 'becoming', name: 'Becoming' },
  { field: 'waltzOfHours', name: 'Waltz of the Hours' },
  { field: 'leJazzHot', name: 'Le Jazz Hot' },
  { field: 'garlandWaltz', name: 'Garland Waltz' }
]

/** Same piece, different labels (enrollment vs archive). */
const PIECE_TITLE_ALIASES = {
  'marzipan shepherdess': 'marzipan maiden'
}

export function pieceTitleKey (title) {
  const key = String(title || '')
    .toLowerCase()
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
  return PIECE_TITLE_ALIASES[key] || key
}

/**
 * Enrollment titles are often the short name; archive titles add the
 * variation / composer (“Sylvia Act III” vs “… Pizzicati”).
 * Require 2+ words on the shorter key so “Blue” does not eat “Blue Bird”.
 */
export function titlesCollapseAsSamePiece (a, b) {
  if (!a || !b) {
    return false
  }
  if (a === b) {
    return true
  }
  const shorter = a.length <= b.length ? a : b
  const longer = a.length <= b.length ? b : a
  if (!longer.startsWith(shorter + ' ')) {
    return false
  }
  const tokens = shorter.split(' ').filter(Boolean)
  return tokens.length >= 2
}

/** Enrollment titles often append the composer; archive titles do not. */
export function findCollapsibleTitleKey (map, key) {
  if (!key) {
    return null
  }
  if (map.has(key)) {
    return key
  }
  for (const existing of map.keys()) {
    if (titlesCollapseAsSamePiece(key, existing)) {
      return existing
    }
  }
  return null
}

/** Small-cast archive titles that are still corps, not showcase groups. */
const ARCHIVE_CORPS_TITLE_PREFIXES = [
  'march of the knights'
]

/**
 * Archive corps casts are large (Shades, Willis, Snowflakes). Small groups
 * like Hot 2 Go stay in Showcase unless the title is a known corps piece.
 */
export function isArchiveGroupPiece (piece) {
  const names = Array.isArray(piece && piece.dancerNames) ? piece.dancerNames : []
  if (names.length > 10) {
    return true
  }
  const key = pieceTitleKey(piece && piece.title)
  if (!key) {
    return false
  }
  return ARCHIVE_CORPS_TITLE_PREFIXES.some(prefix =>
    key === prefix || key.startsWith(prefix + ' ')
  )
}

export function mergeHistoryPieceRow (prev, row) {
  const prevType = prev.pieceType || ''
  const nextType = row.pieceType || ''
  const preferPrevType = prevType && prevType !== 'Showcase piece'
  const shorterTitle = String(prev.title || '').length <= String(row.title || '').length
    ? prev.title
    : row.title
  return {
    ...prev,
    ...row,
    title: shorterTitle || prev.title || row.title,
    id: prev.source === 'enrollment' ? prev.id : (row.source === 'enrollment' ? row.id : prev.id),
    pieceType: preferPrevType
      ? prevType
      : (nextType && nextType !== 'Showcase piece' ? nextType : prevType || nextType),
    songArtist: prev.songArtist || row.songArtist || '',
    competition: prev.competition || row.competition || '',
    performanceRecordingUrl: prev.performanceRecordingUrl || row.performanceRecordingUrl || '',
    role: prev.role || row.role || ''
  }
}

/**
 * Corps / group pieces this dancer was in for a year.
 * @param {object|null} enrollment
 * @param {object} recordings field → URL from performance-recordings.corps
 */
export function corpsPiecesFromEnrollment (enrollment, recordings = {}) {
  if (!enrollment || typeof enrollment !== 'object') {
    return []
  }
  const out = []
  for (const row of CORPS_HISTORY_FIELDS) {
    const info = enrollment[row.field]
    if (!info || !info.participating) {
      continue
    }
    const rec = recordings && recordings[row.field]
    out.push({
      id: row.field,
      name: row.name,
      field: row.field,
      role: String((info && info.role) || '').trim(),
      performanceRecordingUrl: String(rec || '').trim()
    })
  }
  return out
}
